import { useState, useEffect, useRef } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { trackFormStart, trackFormSubmit, trackFormError } from "../utils/analytics";

const initialState = {
  email: "",
  cities: "",
  purpose: "",
  searchCriteria: "",
};

const FORM_SUBMITTED_KEY = 'onbuild_form_submitted';

export const Contact = (props) => {
  const { t, i18n } = useTranslation();
  const [{ email, cities, purpose, searchCriteria }, setState] = useState(initialState);
  const [submitted, setSubmitted] = useState(() => {
    return localStorage.getItem(FORM_SUBMITTED_KEY) === 'true';
  });
  const [captchaToken, setCaptchaToken] = useState(null);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [formStarted, setFormStarted] = useState(false);
  const captchaRef = useRef(null);

  useEffect(() => {
    const isSubmitted = localStorage.getItem(FORM_SUBMITTED_KEY) === 'true';
    if (isSubmitted) {
      setSubmitted(true);
    }
  }, []);

  useEffect(() => {
    // Auto-submit when captcha is verified
    if (captchaToken && showCaptcha) {
      handleActualSubmit();
    }
  }, [captchaToken, showCaptcha]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState((prevState) => ({ ...prevState, [name]: value }));
    
    // Track form start on first interaction
    if (!formStarted) {
      trackFormStart('signup_form');
      setFormStarted(true);
    }
  };
  
  const clearState = () => setState({ ...initialState });
  
  const handleActualSubmit = async () => {
    const formData = new FormData();
    formData.append('email', email);
    formData.append('cities', cities);
    formData.append('purpose', purpose);
    formData.append('searchCriteria', searchCriteria);
    formData.append('language', i18n.language);
    formData.append('h-captcha-response', captchaToken);
    
    try {
      const response = await fetch("https://formspree.io/f/xzzjkjov", {
        method: "POST",
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        // Track successful submission
        trackFormSubmit('signup_form', {
          has_cities: !!cities,
          has_purpose: !!purpose,
          language: i18n.language,
        });
        
        localStorage.setItem(FORM_SUBMITTED_KEY, 'true');
        setSubmitted(true);
        clearState();
        setCaptchaToken(null);
        setShowCaptcha(false);
      } else {
        const data = await response.json();
        console.error("Form submission failed:", data);
        
        // Track form error
        trackFormError('signup_form', 'submission_failed');
        
        alert(t('signup.submissionError') || 'Submission failed. Please try again.');
        if (captchaRef.current) {
          captchaRef.current.resetCaptcha();
        }
        setCaptchaToken(null);
        setShowCaptcha(false);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      
      // Track form error
      trackFormError('signup_form', error.message || 'network_error');
      
      alert(t('signup.submissionError') || 'Submission failed. Please try again.');
      if (captchaRef.current) {
        captchaRef.current.resetCaptcha();
      }
      setCaptchaToken(null);
      setShowCaptcha(false);
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form fields
    if (!email || !searchCriteria) {
      return; // Let HTML5 validation handle it
    }
    
    // Show captcha on first submit attempt
    if (!showCaptcha) {
      setShowCaptcha(true);
    }
  };

  return (
    <div>
      <div id="signup">
        <div className="container">
          <div className="col-md-8 col-md-offset-2">
            <div className="row">
              <div className="section-title text-center">
                <h2>{t('signup.title')}</h2>
                <p>{t('signup.subtitle')}</p>
              </div>
              
              {submitted ? (
                <div className="success-message">
                  <i className="fa fa-check-circle"></i>
                  <h3>
                    {t('signup.successMessage')}
                    {cities && ` ${t('signup.successFor')} ${cities}`}
                  </h3>
                </div>
              ) : (
                <form name="signupForm" onSubmit={handleSubmit}>
                  <div className="form-group">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="form-control"
                      placeholder={t('signup.emailPlaceholder')}
                      required
                      value={email}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="text"
                      id="cities"
                      name="cities"
                      className="form-control"
                      placeholder={t('signup.citiesPlaceholder')}
                      value={cities}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <select
                      id="purpose"
                      name="purpose"
                      className="form-control"
                      value={purpose}
                      onChange={handleChange}
                    >
                      <option value="">{t('signup.purposePlaceholder')}</option>
                      <option value="living">{t('signup.purposeLiving')}</option>
                      <option value="investment">{t('signup.purposeInvestment')}</option>
                      <option value="other">{t('signup.purposeOther')}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <textarea
                      id="searchCriteria"
                      name="searchCriteria"
                      className="form-control"
                      rows="3"
                      placeholder={t('signup.searchCriteriaPlaceholder')}
                      required
                      value={searchCriteria}
                      onChange={handleChange}
                      style={{ resize: 'vertical' }}
                    />
                  </div>
                  <input type="hidden" name="language" value={i18n.language} />
                  {showCaptcha && (
                    <div className="form-group" style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                      <HCaptcha
                        sitekey="d6ca4826-58ae-4ed8-9d2a-d10e41789cda"
                        onVerify={(token) => setCaptchaToken(token)}
                        onExpire={() => setCaptchaToken(null)}
                        languageOverride={i18n.language}
                        ref={captchaRef}
                      />
                    </div>
                  )}
                  <div className="text-center">
                    <button type="submit" className="btn btn-custom btn-lg">
                      {t('signup.button')}
                    </button>
                    <p className="signup-disclaimer">
                      {t('signup.disclaimer')}
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div id="footer">
        <div className="container text-center">
          <p>
            &copy; {new Date().getFullYear()} {t('footer.text')}
          </p>
        </div>
      </div>
    </div>
  );
};
